/** A box in viewport pixels (as `getBoundingClientRect()` reports it). */
export interface NxContextToolbarRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

/**
 * What a context toolbar stays inside: an element (its bounding box), a rect
 * in viewport pixels for a region that is no element of its own (one pane of
 * a split window, the part left of an open sidebar — a `DOMRect` fits), or a
 * function that returns such a rect when the toolbar is placed. `null` (also
 * from the function) means the viewport.
 */
export type NxContextToolbarBoundary =
  | HTMLElement
  | NxContextToolbarRect
  | (() => NxContextToolbarRect | null)
  | null;

/** Whether a boundary is an element (measured with `getBoundingClientRect()`). */
export function isContextToolbarBoundaryElement(
  boundary: NxContextToolbarBoundary | undefined
): boundary is HTMLElement {
  return (
    typeof boundary === 'object' &&
    boundary !== null &&
    typeof (boundary as HTMLElement).getBoundingClientRect === 'function'
  );
}

/**
 * The boundary as a rect in viewport pixels: an element is measured, a
 * function is called, a rect is taken as it is; none is the `fallback` (the
 * viewport).
 */
export function resolveContextToolbarBoundary(
  boundary: NxContextToolbarBoundary | undefined,
  fallback: () => NxContextToolbarRect
): NxContextToolbarRect {
  if (!boundary) return fallback();
  if (typeof boundary === 'function') return boundary() ?? fallback();
  if (isContextToolbarBoundaryElement(boundary)) return boundary.getBoundingClientRect();
  return boundary;
}

export interface NxContextToolbarPlacement {
  /** Viewport position of the toolbar's top left corner. */
  top: number;
  left: number;
  /** Where it ended up relative to the anchor. */
  side: 'top' | 'bottom';
  /** False while the anchor is entirely outside the boundary (scrolled away). */
  visible: boolean;
}

/**
 * Where a context toolbar goes: centred above its anchor, below it when the
 * room above (inside the boundary) is less than its height plus the gap, and
 * pushed sideways so it stays `padding` inside the boundary's left and right
 * edges. A toolbar wider than the boundary sticks to its left edge. Pure — the
 * component feeds it measured rects.
 */
export function placeContextToolbar(
  anchor: NxContextToolbarRect,
  size: { width: number; height: number },
  boundary: NxContextToolbarRect,
  options: { offset: number; padding: number }
): NxContextToolbarPlacement {
  const { offset, padding } = options;
  const roomAbove = anchor.top - boundary.top;
  const side = roomAbove >= size.height + offset ? 'top' : 'bottom';
  const top = side === 'top' ? anchor.top - offset - size.height : anchor.bottom + offset;

  const centred = (anchor.left + anchor.right) / 2 - size.width / 2;
  const min = boundary.left + padding;
  const max = boundary.right - padding - size.width;
  const left = max < min ? min : Math.min(Math.max(centred, min), max);

  const visible =
    anchor.right > boundary.left &&
    anchor.left < boundary.right &&
    anchor.bottom > boundary.top &&
    anchor.top < boundary.bottom;

  return { top, left, side, visible };
}

/** Keys that move the roving tab stop: next/previous, first/last. */
export const ROVING_KEYS = ['ArrowRight', 'ArrowLeft', 'Home', 'End'] as const;

/**
 * The index the roving tab stop moves to for a key, wrapping at both ends
 * (WAI-ARIA toolbar pattern), or null for any other key or an empty toolbar.
 */
export function nextRovingIndex(current: number, count: number, key: string): number | null {
  if (count === 0) return null;
  switch (key) {
    case 'ArrowRight':
      return (current + 1) % count;
    case 'ArrowLeft':
      return (current - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}
